import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { useTranslationKeyboardShortcut } from '../../../../hooks/useTranslationKeybordShortcut';
import { useTranslationContext } from '../../../../context/TranslationContext';
import {
  CloseButton,
  CustomSelect,
  CustomSelectWrapper,
  CustomTextField,
  FlexBox,
  ModalContainer,
  ModalContent,
  ModalHeader,
  Overlay,
  PreviousTranslationBox,
  SaveButtonContainer,
  StyledFormControl,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import {
  useContextStore,
  useProfileStore,
  useTranslationsStore,
} from '../../../../stores/storeRoot';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { getLanguagesByUserLanguage as getLanguageNamesByUserLanguage } from '../../../../i18n/locales/languages';
import {
  cropConnectApiClient,
  CropConnectLanguages,
  TranslationDto,
} from '../../../../services/cropConnectApiClient';

interface Translations {
  [key: string]: string;
}

const reduceTranslationsData = (data: TranslationDto[]): Translations => {
  return data.reduce<Translations>(
    (acc: Translations, translation: TranslationDto) => {
      if (translation.value !== undefined && translation.id !== undefined) {
        Object.entries(translation.value).forEach(([key, value]) => {
          acc[`${translation.id}.${key}`] = value;
        });
      }
      return acc;
    },
    {} as Translations
  );
};

const CustomDraggableModal = () => {
  const { activeKey, setActiveKey, toggleTranslationMode, isTranslationMode } =
    useTranslationContext();
  const { changeUserLanguage, userLanguage } = useProfileStore();
  const { modifyTranslationWithStringKey, getAllowedTranslationLanguages } =
    useTranslationsStore();

  const nodeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newTranslation, setNewTranslation] = useState('');
  const [translations, setTranslations] = useState<Translations>({});
  const [selectedLanguage, setSelectedLanguage] = useState(userLanguage);
  const [previousTranslation, setPreviousTranslation] = useState('');
  const [currentTranslation, setCurrentTranslation] = useState('empty');

  const { businessPartnerAssignment, globalAssignment } = useContextStore();

  const getLanguageOptions = () => {
    const allowedLanguages = getAllowedTranslationLanguages([
      businessPartnerAssignment ?? {},
      globalAssignment ?? {},
    ]);
    const languages = getLanguageNamesByUserLanguage(userLanguage);
    return allowedLanguages.map((al) => ({
      label: `(${al.slice(0, 2).toLowerCase()}) ${languages[al]}`,
      value: al,
    }));
  };

  const languageOptions = getLanguageOptions();

  useEffect(() => {
    if (isTranslationMode) {
      fetchTranslations();
    }
  }, [isTranslationMode]);

  const fetchTranslations = async () => {
    try {
      const response = await cropConnectApiClient.getAllTranslations();
      if (response && Array.isArray(response.data)) {
        const translationsData = reduceTranslationsData(response.data);
        setTranslations(translationsData);
      } else {
        console.error('Unexpected response format:', response);
      }
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    }
  };

  const fetchTranslationsForLanguage = async (
    language: CropConnectLanguages
  ) => {
    try {
      const response =
        await cropConnectApiClient.getTranslationsByLanguage(language);
      if (response && Array.isArray(response.data)) {
        const translationsData = reduceTranslationsData(response.data);
        setTranslations((prevTranslations) => ({
          ...prevTranslations,
          ...translationsData,
        }));
      } else {
        console.error('Unexpected response format:', response);
      }
    } catch (error) {
      console.error(
        `Failed to fetch translations for language: ${language}`,
        error
      );
    }
  };

  useEffect(() => {
    if (translations && activeKey && selectedLanguage) {
      const translationKey = `${selectedLanguage}.${activeKey}`;
      const translation = translations[translationKey];

      if (translation) {
        setCurrentTranslation(translation);
      } else {
        setCurrentTranslation('empty');
      }
    }
  }, [translations, activeKey, selectedLanguage]);

  const closeModal = useCallback(() => {
    toggleTranslationMode();
    setActiveKey(TranslationKeys.empty);
    changeUserLanguage(selectedLanguage);
  }, [
    toggleTranslationMode,
    setActiveKey,
    selectedLanguage,
    changeUserLanguage,
  ]);

  useTranslationKeyboardShortcut(() => {
    if (languageOptions.length > 0) {
      const selectedLanguage =
        languageOptions.find((lo) => lo.value == userLanguage) ??
        languageOptions[0];
      setSelectedLanguage(selectedLanguage.value);
      changeUserLanguage(selectedLanguage.value);
      toggleTranslationMode();
    }
  }, isTranslationMode);

  if (!isTranslationMode) return null;

  const handleCopy = () => {
    if (inputRef.current) {
      navigator.clipboard.writeText(inputRef.current.value);
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = () => {
    setIsDragging(false);
  };

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedLanguage = event.target.value as CropConnectLanguages;
    changeUserLanguage(selectedLanguage);
    setSelectedLanguage(selectedLanguage);
  };

  const handleSaveTranslation = async () => {
    if (activeKey && newTranslation) {
      try {
        setPreviousTranslation(newTranslation);

        await modifyTranslationWithStringKey(
          selectedLanguage,
          activeKey,
          newTranslation
        );

        const translationKey = `${selectedLanguage}.${activeKey}`;
        setTranslations((prevTranslations) => ({
          ...prevTranslations,
          [translationKey]: newTranslation,
        }));

        await fetchTranslationsForLanguage(selectedLanguage);

        setCurrentTranslation(newTranslation);
        setNewTranslation('');
      } catch (error) {
        console.error('Failed to save translation:', error);
      }
    } else {
      console.error('Active key or new translation is missing');
    }
  };

  return (
    <>
      {isTranslationMode && <Overlay onClick={(e) => e.stopPropagation()} />}

      <Draggable
        nodeRef={nodeRef}
        handle=".modal-header"
        bounds="body"
        onStart={() => !isEditing && handleDragStart()}
        onStop={handleDragStop}
        disabled={isDragging || isEditing}
      >
        <ModalContainer ref={nodeRef} style={{ zIndex: 10000 }}>
          <ModalHeader className="modal-header">
            <DragIndicatorIcon />
            <Typography variant="h6">Translation Helper</Typography>
            <CloseButton onClick={closeModal} size="small">
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <PreviousTranslationBox>
            {previousTranslation && (
              <Typography variant="subtitle1" color="textSecondary">
                previously changed value: {previousTranslation}
              </Typography>
            )}
          </PreviousTranslationBox>
          <ModalContent>
            <StyledFormControl>
              <CustomSelectWrapper>
                <CustomSelect
                  ref={selectRef}
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </CustomSelect>
              </CustomSelectWrapper>
            </StyledFormControl>
            <CustomTextField
              label="Translation Key"
              value={activeKey || 'empty'}
              disabled
            />
            <FlexBox>
              <CustomTextField
                label="Current Translation"
                value={currentTranslation || 'empty'}
                inputRef={inputRef}
                disabled
              />
              <IconButton onClick={handleCopy} size="small">
                <FileCopyIcon />
              </IconButton>
            </FlexBox>
            <input
              type="text"
              value={newTranslation}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                const target = e.target as HTMLInputElement;
                target.focus();
              }}
              onFocus={(e) => {
                setIsEditing(true);
                e.stopPropagation();
              }}
              onBlur={(e) => {
                setIsEditing(false);
                e.stopPropagation();
              }}
              onChange={(e) => {
                setNewTranslation(e.target.value);
                e.stopPropagation();
              }}
              placeholder="New Translation String"
              style={{
                width: '100%',
                height: '56px',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #bdbdbd',
                fontSize: '16px',
                marginBottom: '16px',
                zIndex: '9999999999999',
              }}
            />
            <SaveButtonContainer>
              <ActionButton
                variant={'accept'}
                buttonText={'Save Translation'}
                fullWidth={true}
                onClickAction={handleSaveTranslation}
                disabled={
                  !activeKey || activeKey === 'empty' || !newTranslation
                }
              />
            </SaveButtonContainer>
          </ModalContent>
        </ModalContainer>
      </Draggable>
    </>
  );
};

export default CustomDraggableModal;
